import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  fetchCatalogItems,
  selectCatalogItemsLoading,
  selectUnpublishedCatalogItems,
} from "../../../redux/catalog";
import { removeConvertible } from "../../../redux/convertibleProducts";
import { getUpgradePromoStandardsProductTaskStatus } from "../../../actions/item";
import type { CatalogItemsFilters } from "./CatalogPage";

export interface UpgradeQueuedPayload {
  order_id?: string | null;
  item_id: string;
  item_name?: string;
  promo_api_name?: string | null;
  supplier_code?: string | null;
}

interface UpgradingEntry {
  order_id: string;
  item_name?: string;
  promo_api_name?: string | null;
  supplier_code?: string | null;
  status: string;
}

interface UpgradeFailedEntry {
  order_id: string;
  error_message?: string;
}

const UPGRADE_POLL_INTERVAL_MS = 10000;

export function useEpoUpgradeTracker({
  catalogId,
  filters,
}: {
  catalogId: string | null;
  filters: CatalogItemsFilters;
}) {
  const dispatch = useDispatch();

  const [upgradingByItemId, setUpgradingByItemId] = useState<
    Record<string, UpgradingEntry>
  >({});
  const [upgradeFailedByItemId, setUpgradeFailedByItemId] = useState<
    Record<string, UpgradeFailedEntry>
  >({});

  const [upgradeStatusLoading, setUpgradeStatusLoading] = useState(true);

  // Refs keep the interval callback reading current values instead of the
  // closure captured when the interval was created.
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Guards against a new poll cycle starting before the previous async pass
  // resolves, which would duplicate terminal success toasts and refreshes.
  const isPollingRef = useRef(false);
  const upgradingRef = useRef(upgradingByItemId);
  upgradingRef.current = upgradingByItemId;
  const pollFnRef = useRef<() => void>(() => {});

  const unpublishedItems = useSelector(selectUnpublishedCatalogItems);
  const unpublishedRef = useRef(unpublishedItems);
  unpublishedRef.current = unpublishedItems;

  const itemsLoading = useSelector(selectCatalogItemsLoading);

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // Bootstrap (catalog mount) runs at most once per catalogId;
  // the in-flight guard stops the async pass from overlapping with itself.
  const didBootstrapForCatalogIdRef = useRef<string | null>(null);
  const isBootstrappingRef = useRef(false);

  const clearPollInterval = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  const removeUpgrading = useCallback((itemId: string) => {
    setUpgradingByItemId((prev) => {
      if (!(itemId in prev)) return prev;
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
  }, []);

  // Drop the convertible row immediately when an upgrade succeeds,
  // unless another still-unpublished, non-PS item shares the same supplier+promo.
  const maybeRemoveConvertible = useCallback(
    (entry: UpgradingEntry, deletedItemId?: string) => {
      const { promo_api_name, supplier_code } = entry;
      if (!promo_api_name || !supplier_code) return;

      const stillUsed = unpublishedRef.current.some(
        (i) =>
          i.supplier_code === supplier_code &&
          i.promo_api_name === promo_api_name &&
          i.copied_from !== "ps-products" &&
          i.item_id !== deletedItemId,
      );
      if (!stillUsed) {
        dispatch(
          removeConvertible({
            promo_api_name,
            item_sku: supplier_code,
          }) as any,
        );
      }
    },
    [dispatch],
  );

  const pollUpgrades = useCallback(async () => {
    if (isPollingRef.current) return;

    const entries = Object.entries(upgradingRef.current);
    if (entries.length === 0) {
      clearPollInterval();
      return;
    }

    isPollingRef.current = true;
    try {
      await Promise.allSettled(
      entries.map(async ([item_id, info]) => {
        try {
          const json: any = await dispatch(
            getUpgradePromoStandardsProductTaskStatus(
              item_id,
              info.order_id,
            ) as any,
          );
          const up = json?.upgrade;

          if (!up?.has_task) {
            removeUpgrading(item_id);
            return;
          }

          if (up.status === "PENDING" || up.status === "PROCESSING") {
            setUpgradingByItemId((prev) =>
              prev[item_id]
                ? {
                    ...prev,
                    [item_id]: {
                      ...prev[item_id],
                      status: up.status,
                    },
                  }
                : prev,
            );
            return;
          }

          if (up.status === "FAILED") {
            setUpgradeFailedByItemId((prev) => ({
              ...prev,
              [item_id]: {
                order_id: info.order_id,
                error_message: up.error_message,
              },
            }));
            removeUpgrading(item_id);
            return;
          }

          if (up.status === "SUCCESS") {
            removeUpgrading(item_id);

            const label = json?.item?.item_name || info.item_name || "product";
            toast.success(`Upgrade completed for ${label}`);

            maybeRemoveConvertible(info, json?.deleted_item_id);

            if (catalogId) {
              dispatch(
                fetchCatalogItems({
                  catalogId,
                  hidden: 1,
                  silent: true,
                  ...filtersRef.current,
                }) as any,
              );
            }
          }
        } catch {
          removeUpgrading(item_id);
        }
      }),
      );
    } finally {
      isPollingRef.current = false;
    }
  }, [
    dispatch,
    clearPollInterval,
    removeUpgrading,
    maybeRemoveConvertible,
    catalogId,
  ]);

  pollFnRef.current = pollUpgrades;

  const ensureUpgradePolling = useCallback(() => {
    if (pollIntervalRef.current) return;
    pollIntervalRef.current = setInterval(() => {
      pollFnRef.current();
    }, UPGRADE_POLL_INTERVAL_MS);
  }, []);

  // Stop polling once nothing is in flight, and clean up on unmount
  useEffect(() => {
    if (Object.keys(upgradingByItemId).length === 0) {
      clearPollInterval();
    }
  }, [upgradingByItemId, clearPollInterval]);

  useEffect(() => clearPollInterval, [clearPollInterval]);

  // Query backend task status for every convertible (non-PS) unpublished item
  const bootstrapUpgradeStatuses = useCallback(async () => {
    const empty = {
      upgrading: {} as Record<string, UpgradingEntry>,
      failed: {} as Record<string, UpgradeFailedEntry>,
    };
    if (!catalogId) return empty;

    const candidates = unpublishedRef.current.filter(
      (i) => i.copied_from !== "ps-products" && i.promo_api_name,
    );
    if (candidates.length === 0) return empty;

    const results = await Promise.allSettled(
      candidates.map((item) =>
        dispatch(
          getUpgradePromoStandardsProductTaskStatus(
            item.item_id,
            catalogId,
          ) as any,
        ),
      ),
    );

    const upgrading: Record<string, UpgradingEntry> = {};
    const failed: Record<string, UpgradeFailedEntry> = {};
    results.forEach((r, idx) => {
      if (r.status !== "fulfilled" || !r.value) return;
      const up = (r.value as any)?.upgrade;
      if (!up?.has_task) return;

      const item = candidates[idx];
      if (up.status === "PENDING" || up.status === "PROCESSING") {
        upgrading[item.item_id] = {
          order_id: catalogId,
          item_name: item.item_name,
          promo_api_name: item.promo_api_name,
          supplier_code: item.supplier_code,
          status: up.status,
        };
      } else if (up.status === "FAILED") {
        failed[item.item_id] = {
          order_id: catalogId,
          error_message: up.error_message,
        };
      }
    });
    return { upgrading, failed };
  }, [catalogId, dispatch]);

  const maybeBootstrapUpgradeStatuses = useCallback(async () => {
    if (!catalogId) return;
    if (isBootstrappingRef.current) return;
    if (didBootstrapForCatalogIdRef.current === catalogId) return;

    isBootstrappingRef.current = true;
    try {
      const { upgrading, failed } = await bootstrapUpgradeStatuses();
      const hasUpgrading = Object.keys(upgrading).length > 0;
      const hasFailed = Object.keys(failed).length > 0;

      // Merge rather than replace so an upgrade queued during this async
      // pass (onUpgradeQueued) isn't clobbered.
      if (hasUpgrading) {
        setUpgradingByItemId((prev) => ({ ...prev, ...upgrading }));
      }
      if (hasFailed) {
        setUpgradeFailedByItemId((prev) => ({ ...prev, ...failed }));
      }
      if (hasUpgrading) {
        ensureUpgradePolling();
      }
      didBootstrapForCatalogIdRef.current = catalogId;
    } finally {
      isBootstrappingRef.current = false;
      setUpgradeStatusLoading(false);
    }
  }, [
    catalogId,
    bootstrapUpgradeStatuses,
    ensureUpgradePolling,
  ]);

  useEffect(() => {
    setUpgradeStatusLoading(true);
  }, [catalogId]);

  useEffect(() => {
    if (!catalogId || itemsLoading) return;
    void maybeBootstrapUpgradeStatuses();
  }, [
    catalogId,
    itemsLoading,
    maybeBootstrapUpgradeStatuses,
  ]);

  const onUpgradeQueued = useCallback(
    ({
      order_id,
      item_id,
      item_name,
      promo_api_name,
      supplier_code,
    }: UpgradeQueuedPayload) => {
      const resolvedOrderId = order_id ?? catalogId;
      if (!resolvedOrderId) return;

      setUpgradingByItemId((prev) => ({
        ...prev,
        [item_id]: {
          order_id: resolvedOrderId,
          item_name,
          promo_api_name,
          supplier_code,
          status: "PENDING",
        },
      }));
      setUpgradeFailedByItemId((prev) => {
        if (!(item_id in prev)) return prev;
        const next = { ...prev };
        delete next[item_id];
        return next;
      });
      ensureUpgradePolling();
    },
    [catalogId, ensureUpgradePolling],
  );

  const clearUpgradeFailed = useCallback((itemId: string) => {
    setUpgradeFailedByItemId((prev) => {
      if (!(itemId in prev)) return prev;
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
  }, []);

  return {
    upgradingByItemId,
    upgradeFailedByItemId,
    upgradeStatusLoading,
    onUpgradeQueued,
    clearUpgradeFailed,
  };
}
